// Planilha Gran — regra pura (sem React, sem Supabase), para rodar em `node --test`:
//   node --experimental-strip-types --test views/Financeiro/PlanilhaGran/__tests__/planilhaGranModel.test.ts
//
// A Vigor monta, a partir das faturas do sistema, a planilha mensal dos condomínios da
// Gran (modelo: "Gran Energia - Planilha 07 2026.xlsx": NOME, UC, BOLETO GRAN, EQUATORIAL,
// COMISSÃO, LINK) e manda a versão dela para a Gran. Quem entra na planilha é a lista de
// `planilha_gran_itens` (ver supabase/migrations/20260925120000_planilha_gran.sql); os
// valores saem da fatura de cada UC no mês escolhido.
//
// O que a planilha antiga da Gran errava e esta regra pega sozinha (medido no modelo
// de 07/2026): UC repetida, UC cancelada ainda cobrada, total de comissão que parava na
// linha 23, e linha com boleto igual ao valor da Equatorial (comissão zero).

import type { GridModel } from '../../Gestao/Extracao/planilha/faturaGridModel';
import { dateToSerial } from '../../Gestao/Extracao/planilha/faturaGridModel.ts';
import { normalizeMesReferencia, MESES_ABREV } from '../../../src/lib/mesReferencia.ts';
import { faturaEntraNaDuplicata, normalizarUcFatura } from '../../../src/lib/duplicataFatura.ts';

/** Comissão da planilha: (boleto − Equatorial) × 5%. De quem ela é ainda não foi decidido
 *  (reunião de 25/09/2026) — por enquanto a tela só reproduz a conta do modelo da Gran. */
export const PERCENTUAL_COMISSAO = 0.05;

/** Diferença abaixo disto entre boleto e Equatorial conta como "boleto só da Equatorial".
 *  Cinco centavos: o arredondamento do boleto único deixa 1 centavo de sobra (UC
 *  2.622.618.012-03, AGO/2026: 3.118,09 × 3.118,08). */
const TOLERANCIA_CENTAVOS = 0.05;

export type OrigemItem = 'planilha' | 'extra';

/** O que a Gran conferiu na UC (#6116, planilha "ASSOCIADOS GRAN - GUILHERME"). Referência:
 *  a tela compara com o cadastro e aponta a diferença, sem corrigir nada. */
export interface ConferenciaGran {
  fonte?: string;
  conferido_em?: string;
  /** 0..1 (1 = 100%). */
  desconto?: number | null;
  no_rateio?: boolean | null;
  vai_trocar_para_vigor?: boolean | null;
  titular_documento?: string | null;
  cliente_documento?: string | null;
}

export interface ItemPlanilhaGran {
  id: number;
  unidade_consumidora_id: number;
  numero_uc: string;
  grupo: string;
  origem: OrigemItem;
  /** Anotação da conferência com a Gran (instruções para a equipe). Só registro. */
  anotacao?: string | null;
  conferencia?: ConferenciaGran | null;
}

export interface UcPlanilhaGran {
  id: number;
  numero_uc?: string | null;
  outro_numero_uc?: string | null;
  nome_cliente?: string | null;
  situacao?: string | null;
  empresa_parceira_id?: string | null;
  desconto_tarifa?: number | string | null;
  cpf_cnpj_titular?: string | null;
  cpf_cnpj_cliente?: string | null;
}

/** Situações em que a UC está recebendo energia (no rateio). */
const SITUACOES_NO_RATEIO = ['Ativa', '1° Ciclo', '2° Ciclo', 'Reativacao'];

const pct = (v: number) => `${Math.round(v * 1000) / 10}%`.replace('.', ',');
const soDigitos = (s: unknown) => String(s ?? '').replace(/\D/g, '');

/**
 * Onde o cadastro está diferente do que a Gran conferiu. Uma frase por diferença, para ir
 * na coluna "Conferir" — quem corrige o cadastro é a equipe; o aviso some quando bater.
 */
export function divergenciasConferencia(conf: ConferenciaGran | null | undefined, uc: UcPlanilhaGran | undefined): string[] {
  if (!conf || !uc) return [];
  const out: string[] = [];
  if (typeof conf.desconto === 'number') {
    const cad = uc.desconto_tarifa === null || uc.desconto_tarifa === undefined || uc.desconto_tarifa === '' ? null : Number(uc.desconto_tarifa);
    if (cad === null || !Number.isFinite(cad)) out.push(`Desconto vazio no cadastro; a Gran conferiu ${pct(conf.desconto)}`);
    else if (Math.abs(cad - conf.desconto) > 0.001) out.push(`Desconto ${pct(cad)} no cadastro; a Gran conferiu ${pct(conf.desconto)}`);
  }
  const sit = (uc.situacao || '').trim();
  if (conf.no_rateio === false && SITUACOES_NO_RATEIO.includes(sit)) out.push(`A Gran conferiu fora do rateio; cadastro está ${sit}`);
  if (conf.no_rateio === true && sit && !SITUACOES_NO_RATEIO.includes(sit) && sit !== 'Cancelada') out.push(`A Gran conferiu no rateio; cadastro está ${sit}`);
  if (conf.titular_documento && soDigitos(conf.titular_documento) !== soDigitos(uc.cpf_cnpj_titular)) out.push('Titular da conta diferente do conferido pela Gran');
  if (conf.cliente_documento && soDigitos(conf.cliente_documento) !== soDigitos(uc.cpf_cnpj_cliente)) out.push('CPF/CNPJ do cliente diferente do conferido pela Gran');
  return out;
}

/** Quando e de onde veio a conferência (para uma legenda curta, não uma coluna nova —
 *  ponto do systems-thinker em #6116). Pega a combinação mais comum entre os itens, já
 *  que a carteira inteira normalmente vem de uma leva só. `null` = nenhum item conferido. */
export function resumoConferenciaGran(itens: ItemPlanilhaGran[]): { data: string; fonte: string } | null {
  const contagem = new Map<string, number>();
  for (const i of itens) {
    const c = i.conferencia;
    if (!c?.conferido_em) continue;
    const chave = `${c.conferido_em}|${c.fonte || ''}`;
    contagem.set(chave, (contagem.get(chave) || 0) + 1);
  }
  if (contagem.size === 0) return null;
  const [chave] = Array.from(contagem.entries()).sort((a, b) => b[1] - a[1])[0];
  const [data, fonte] = chave.split('|');
  return { data, fonte };
}

export interface FaturaPlanilhaGran {
  id: number | string;
  uc_codigo?: string | null;
  mes_referencia?: string | null;
  status_fatura?: string | null;
  status_boleto_vigor?: string | null;
  classificacao?: string | null;
  valor_boleto_vigor?: number | string | null;
  valor_emissao_boleto_unico?: number | string | null;
  valor_fatura_distribuidora?: number | string | null;
  equatorial_quitada_pelo_cliente?: boolean | null;
  vencimento_boleto?: string | null;
  url_boleto_combinado?: string | null;
  url_boleto?: string | null;
  excluido_em?: string | null;
  /** 'sandbox' = emissão de teste no Asaas: nunca entra na planilha. */
  ambiente?: string | null;
}

/** Em que pé está a UC no mês. É o filtro principal da tela (como Confere/Revisar/Falhou
 *  na extração) e a ordem é a do fluxo, da que falta mais para a que já terminou. */
export type EtapaGran = 'sem_fatura' | 'em_andamento' | 'sem_cobranca' | 'pronta' | 'emitida' | 'paga' | 'cancelada';

export const ETAPAS: { key: EtapaGran; rotulo: string; descricao: string }[] = [
  { key: 'sem_fatura', rotulo: 'Sem fatura', descricao: 'Nenhuma fatura desta UC no mês' },
  { key: 'em_andamento', rotulo: 'Em andamento', descricao: 'Fatura ainda sem boleto calculado' },
  { key: 'sem_cobranca', rotulo: 'Sem cobrança', descricao: 'Boleto calculado em zero (mês sem energia da Gran)' },
  { key: 'pronta', rotulo: 'Pronta p/ emitir', descricao: 'Boleto calculado, falta emitir' },
  { key: 'emitida', rotulo: 'Emitida', descricao: 'Boleto emitido, aguardando pagamento' },
  { key: 'paga', rotulo: 'Paga', descricao: 'Boleto pago ou quitado' },
  { key: 'cancelada', rotulo: 'Boleto cancelado', descricao: 'O boleto do mês foi cancelado' },
];

export const rotuloEtapa = (e: EtapaGran) => ETAPAS.find(x => x.key === e)?.rotulo ?? e;

/** Status do boleto como a planilha mostra: "Em aberto" vira "A vencer" (pedido de
 *  25/09/2026 — só o rótulo; no banco continua `Em aberto`). */
export const rotuloStatusBoleto = (s: string | null | undefined) => {
  const v = (s || '').trim();
  return v === 'Em aberto' ? 'A vencer' : v;
};

/** Colunas da aba UCs que ficam só na tela e não vão para o Excel de conferência. */
export const COLUNAS_SO_NA_TELA = ['Fatura nº', 'Origem'];

export interface LinhaPlanilhaGran {
  itemId: number;
  grupo: string;
  origem: OrigemItem;
  ucId: number;
  numeroUc: string;
  nomeCliente: string;
  faturaId: number | null;
  statusFatura: string;
  statusBoleto: string;
  /** ISO (YYYY-MM-DD) ou ''. */
  vencimento: string;
  etapa: EtapaGran;
  /** Valor do boleto único (energia + Equatorial). `null` = ainda não dá para saber. */
  boletoGran: number | null;
  /** O boleto ainda não saiu: o valor é o calculado, e pode mudar até a emissão. */
  boletoPrevisto: boolean;
  equatorial: number | null;
  comissao: number | null;
  link: string | null;
  /** Por que a linha pede conferência. Vazio = nada a conferir. */
  motivos: string[];
  /** Anotação da conferência com a Gran (só registro). */
  anotacao: string;
  /** Quando não há valor, a frase que vai no lugar do número (nunca "R$ 0,00"). */
  semValorPorque: string;
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const round2 = (v: number) => Math.round(v * 100) / 100;

const STATUS_EM_ANDAMENTO = ['Aguardando Upload', 'Fatura disponível', 'Aguardando validação', 'Fatura validada'];
const STATUS_EMITIDA = ['Com boleto emitido / A enviar', 'Cobrança enviada'];
const STATUS_FORA = ['Excluído', 'Cancelada'];

/** Fatura que conta para a planilha: nem excluída, nem cancelada, nem acordo avulso. */
export function faturaConta(f: FaturaPlanilhaGran): boolean {
  if (f.excluido_em) return false;
  // Faturas de teste do Asaas vivem na mesma UC (medido: a 3.445.808.012-12 tem uma de
  // R$ 10 "paga" em SET/2026 pelo outro número dela) e tomariam o lugar da real.
  if ((f.ambiente || '').trim() === 'sandbox') return false;
  if (STATUS_FORA.includes((f.status_fatura || '').trim())) return false;
  return (f.classificacao || '').trim().toUpperCase() !== 'AVULSO';
}

export function etapaDaFatura(f: FaturaPlanilhaGran | null): EtapaGran {
  if (!f) return 'sem_fatura';
  const sf = (f.status_fatura || '').trim();
  const sb = (f.status_boleto_vigor || '').trim();
  // Boleto calculado em zero não é cobrança (mesma regra de `derivarStatusBoleto`):
  // mostrar "Emitida R$ 0,00" faria a linha parecer um boleto que ninguém pagou.
  // Só com zero GRAVADO: valor ausente não é "calculado em zero" (aí a linha segue na
  // etapa do status e mostra "sem valor" no lugar do número).
  const calculadoOuEmitido = STATUS_EMITIDA.includes(sf) || sf === 'Com boleto calculado';
  const emissao = num(f.valor_emissao_boleto_unico);
  const energia = num(f.valor_boleto_vigor);
  if (calculadoOuEmitido && (emissao !== null || energia !== null) && !((emissao ?? 0) > 0) && !((energia ?? 0) > 0)) {
    return 'sem_cobranca';
  }
  if (STATUS_EMITIDA.includes(sf)) {
    if (sb === 'Pago' || sb === 'Quitado') return 'paga';
    if (sb === 'Cancelado') return 'cancelada';
    return 'emitida';
  }
  if (sf === 'Com boleto calculado') return 'pronta';
  if (STATUS_EM_ANDAMENTO.includes(sf) || !sf) return 'em_andamento';
  return 'em_andamento';
}

/** Peso para escolher a fatura "da vez" quando a UC tem mais de uma no mês. */
const PESO_ETAPA: Record<EtapaGran, number> = {
  paga: 6, emitida: 5, pronta: 4, sem_cobranca: 3, em_andamento: 3, cancelada: 2, sem_fatura: 0,
};

/** Chaves de UC (número principal e o outro número) no formato de `uc_codigo_norm`. */
function chavesDaUc(uc: UcPlanilhaGran | undefined, item: ItemPlanilhaGran): string[] {
  const chaves = [uc?.numero_uc, uc?.outro_numero_uc, item.numero_uc]
    .map(normalizarUcFatura)
    .filter(Boolean);
  return Array.from(new Set(chaves));
}

/**
 * Valores da linha a partir da fatura. Toda UC da Gran é boleto único (78 de 78 em
 * 25/09/2026): o boleto cobra a energia (valor_boleto_vigor) mais a conta da Equatorial.
 *  - Emitido: vale o que foi para o banco (`valor_emissao_boleto_unico`).
 *  - Calculado e não emitido: previsão = energia + Equatorial (a não ser que o cliente já
 *    tenha pago a Equatorial por fora), marcada como prevista.
 *  - Antes disso: sem valor, com o motivo no lugar.
 */
export function valoresDaFatura(f: FaturaPlanilhaGran | null, etapa: EtapaGran): {
  boletoGran: number | null; boletoPrevisto: boolean; equatorial: number | null; semValorPorque: string;
} {
  if (!f) return { boletoGran: null, boletoPrevisto: false, equatorial: null, semValorPorque: 'Sem fatura no mês' };
  // Zero na conta da Equatorial é "ainda não chegou" (fatura aguardando o upload), não uma
  // conta de R$ 0,00 — sai em branco na tela e no Excel.
  const bruto = num(f.valor_fatura_distribuidora);
  const equatorial = bruto !== null && bruto > 0 ? bruto : null;
  if (etapa === 'sem_cobranca') {
    return { boletoGran: null, boletoPrevisto: false, equatorial, semValorPorque: 'Sem cobrança no mês (boleto calculado em zero)' };
  }
  if (etapa === 'em_andamento') {
    return { boletoGran: null, boletoPrevisto: false, equatorial, semValorPorque: `Boleto ainda não calculado (${f.status_fatura || 'sem status'})` };
  }
  if (etapa === 'pronta') {
    const energia = num(f.valor_boleto_vigor);
    if (energia === null) return { boletoGran: null, boletoPrevisto: false, equatorial, semValorPorque: 'Boleto calculado sem valor' };
    const parteEquatorial = f.equatorial_quitada_pelo_cliente ? 0 : (equatorial ?? 0);
    return { boletoGran: round2(energia + parteEquatorial), boletoPrevisto: true, equatorial, semValorPorque: '' };
  }
  const emitido = num(f.valor_emissao_boleto_unico) ?? num(f.valor_boleto_vigor);
  if (emitido === null) return { boletoGran: null, boletoPrevisto: false, equatorial, semValorPorque: 'Boleto emitido sem valor registrado' };
  return { boletoGran: round2(emitido), boletoPrevisto: false, equatorial, semValorPorque: '' };
}

export function comissaoDe(boletoGran: number | null, equatorial: number | null): number | null {
  if (boletoGran === null || equatorial === null) return null;
  return round2((boletoGran - equatorial) * PERCENTUAL_COMISSAO);
}

/** Vencimento como ISO (YYYY-MM-DD), ou '' se não houver. */
const isoData = (v: unknown) => {
  const s = String(v ?? '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
};

/**
 * Uma linha por UC da lista, no mês pedido. `granId` é a empresa da planilha: UC do
 * cadastro que não é dela vira motivo de revisão (não some — cadastro é com outra pessoa).
 */
export function montarLinhasGran(params: {
  itens: ItemPlanilhaGran[];
  ucs: UcPlanilhaGran[];
  faturas: FaturaPlanilhaGran[];
  mes: string;
  granId: string;
}): LinhaPlanilhaGran[] {
  const { itens, ucs, faturas, granId } = params;
  const mes = normalizeMesReferencia(params.mes);
  const ucPorId = new Map(ucs.map(u => [Number(u.id), u]));

  // Faturas do mês indexadas pela UC normalizada — uma passada só na base inteira.
  const doMes = new Map<string, FaturaPlanilhaGran[]>();
  for (const f of faturas) {
    if (normalizeMesReferencia(f.mes_referencia) !== mes) continue;
    const k = normalizarUcFatura(f.uc_codigo);
    if (!k) continue;
    const lista = doMes.get(k);
    if (lista) lista.push(f); else doMes.set(k, [f]);
  }

  return itens.map((item): LinhaPlanilhaGran => {
    const uc = ucPorId.get(Number(item.unidade_consumidora_id));
    const vistas = new Set<string | number>();
    const candidatas: FaturaPlanilhaGran[] = [];
    for (const k of chavesDaUc(uc, item)) {
      for (const f of doMes.get(k) || []) {
        if (vistas.has(f.id)) continue;
        vistas.add(f.id);
        candidatas.push(f);
      }
    }
    const validas = candidatas.filter(faturaConta);
    const escolhida = validas
      .slice()
      .sort((a, b) => (PESO_ETAPA[etapaDaFatura(b)] - PESO_ETAPA[etapaDaFatura(a)]) || (Number(b.id) - Number(a.id)))[0] ?? null;

    const etapa = etapaDaFatura(escolhida);
    const v = valoresDaFatura(escolhida, etapa);
    const comissao = comissaoDe(v.boletoGran, v.equatorial);

    const motivos: string[] = [];
    if (!uc) motivos.push('UC não encontrada no cadastro');
    else {
      if (uc.empresa_parceira_id !== granId) motivos.push('UC não está vinculada à Gran no cadastro');
      if ((uc.situacao || '').trim() === 'Cancelada') motivos.push('UC cancelada no cadastro');
    }
    const concorrentes = validas.filter(f => faturaEntraNaDuplicata(f));
    if (concorrentes.length > 1) motivos.push(`${concorrentes.length} faturas ativas no mês`);
    if (etapa === 'cancelada') motivos.push('Boleto do mês cancelado');
    if (v.boletoGran !== null && v.equatorial !== null && v.equatorial > 0
        && Math.abs(v.boletoGran - v.equatorial) < TOLERANCIA_CENTAVOS) {
      motivos.push('Boleto igual à conta da Equatorial (sem parte da Gran)');
    }
    if (v.boletoGran !== null && v.equatorial !== null && v.boletoGran < v.equatorial - TOLERANCIA_CENTAVOS) {
      motivos.push('Boleto menor que a conta da Equatorial');
    }
    motivos.push(...divergenciasConferencia(item.conferencia, uc));

    return {
      itemId: item.id,
      grupo: item.grupo,
      origem: item.origem,
      ucId: Number(item.unidade_consumidora_id),
      numeroUc: uc?.numero_uc || item.numero_uc,
      nomeCliente: uc?.nome_cliente || '',
      faturaId: escolhida ? Number(escolhida.id) : null,
      statusFatura: escolhida?.status_fatura || '',
      statusBoleto: rotuloStatusBoleto(escolhida?.status_boleto_vigor),
      vencimento: isoData(escolhida?.vencimento_boleto),
      etapa,
      boletoGran: v.boletoGran,
      boletoPrevisto: v.boletoPrevisto,
      equatorial: v.equatorial,
      comissao,
      link: etapa === 'emitida' || etapa === 'paga'
        ? (escolhida?.url_boleto_combinado || escolhida?.url_boleto || null)
        : null,
      motivos,
      anotacao: (item.anotacao || '').trim(),
      semValorPorque: v.semValorPorque,
    };
  }).sort((a, b) => a.grupo.localeCompare(b.grupo, 'pt-BR') || a.numeroUc.localeCompare(b.numeroUc));
}

// ─────────────────────────── filtros ───────────────────────────

export interface FiltroPlanilhaGran {
  etapa: EtapaGran | 'todas' | 'revisar';
  grupo: string; // '' = todos
  origem: OrigemItem | 'todas';
  busca: string;
}

export const FILTRO_VAZIO: FiltroPlanilhaGran = { etapa: 'todas', grupo: '', origem: 'todas', busca: '' };

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function filtrarLinhas(linhas: LinhaPlanilhaGran[], filtro: FiltroPlanilhaGran): LinhaPlanilhaGran[] {
  const termo = semAcento(filtro.busca.trim());
  const termoUc = filtro.busca.replace(/\D/g, '').replace(/^0+/, '');
  return linhas.filter(l => {
    if (filtro.etapa === 'revisar' ? l.motivos.length === 0 : filtro.etapa !== 'todas' && l.etapa !== filtro.etapa) return false;
    if (filtro.grupo && l.grupo !== filtro.grupo) return false;
    if (filtro.origem !== 'todas' && l.origem !== filtro.origem) return false;
    if (termo) {
      const noTexto = semAcento(`${l.grupo} ${l.nomeCliente} ${l.anotacao}`).includes(termo);
      const naUc = termoUc.length >= 3 && normalizarUcFatura(l.numeroUc).includes(termoUc);
      const naFatura = termoUc.length > 0 && l.faturaId !== null && String(l.faturaId) === termoUc;
      if (!noTexto && !naUc && !naFatura) return false;
    }
    return true;
  });
}

/** Contagem por etapa (e "revisar") do conjunto já recortado por grupo/origem/busca —
 *  os números dos chips batem com o que aparece ao clicar neles. */
export function contarEtapas(linhas: LinhaPlanilhaGran[]): Record<EtapaGran | 'todas' | 'revisar', number> {
  const c = { todas: linhas.length, revisar: 0, sem_fatura: 0, em_andamento: 0, sem_cobranca: 0, pronta: 0, emitida: 0, paga: 0, cancelada: 0 };
  for (const l of linhas) {
    c[l.etapa]++;
    if (l.motivos.length) c.revisar++;
  }
  return c;
}

// ─────────────────────────── totais e condomínios ───────────────────────────

export interface TotaisGran {
  ucs: number;
  boletoGran: number;
  equatorial: number;
  comissao: number;
  /** UCs sem valor de boleto — o total não inclui essas. */
  semValor: number;
}

/** Soma TODAS as linhas recebidas (a planilha da Gran somava só até a linha 23). */
export function totalizar(linhas: LinhaPlanilhaGran[]): TotaisGran {
  const t = { ucs: linhas.length, boletoGran: 0, equatorial: 0, comissao: 0, semValor: 0 };
  for (const l of linhas) {
    if (l.boletoGran === null) t.semValor++;
    else t.boletoGran += l.boletoGran;
    t.equatorial += l.equatorial ?? 0;
    t.comissao += l.comissao ?? 0;
  }
  return { ...t, boletoGran: round2(t.boletoGran), equatorial: round2(t.equatorial), comissao: round2(t.comissao) };
}

export interface ResumoCondominio extends TotaisGran {
  grupo: string;
  etapas: Record<EtapaGran, number>;
  revisar: number;
  origem: OrigemItem | 'misto';
}

export function resumirCondominios(linhas: LinhaPlanilhaGran[]): ResumoCondominio[] {
  const porGrupo = new Map<string, LinhaPlanilhaGran[]>();
  for (const l of linhas) {
    const lista = porGrupo.get(l.grupo);
    if (lista) lista.push(l); else porGrupo.set(l.grupo, [l]);
  }
  return Array.from(porGrupo.entries())
    .map(([grupo, ls]) => {
      const etapas = { sem_fatura: 0, em_andamento: 0, sem_cobranca: 0, pronta: 0, emitida: 0, paga: 0, cancelada: 0 };
      ls.forEach(l => { etapas[l.etapa]++; });
      const origens = new Set(ls.map(l => l.origem));
      return {
        grupo,
        ...totalizar(ls),
        etapas,
        revisar: ls.filter(l => l.motivos.length).length,
        origem: origens.size > 1 ? 'misto' as const : ls[0].origem,
      };
    })
    .sort((a, b) => a.grupo.localeCompare(b.grupo, 'pt-BR'));
}

// ─────────────────────────── grids (tela = Excel) ───────────────────────────

/** Colunas da aba UCs: depois do motivo, NOME/UC/BOLETO/EQUATORIAL/COMISSÃO do modelo
 *  da Gran, o que a Vigor acrescenta, e o link por último. */
export function gridUcs(linhas: LinhaPlanilhaGran[]): GridModel {
  const columns: GridModel['columns'] = [
    { label: 'Conferir', type: 'txt' },
    { label: 'NOME', type: 'txt' },
    { label: 'UC', type: 'txt' },
    { label: 'BOLETO GRAN (R$)', type: 'num' },
    { label: 'EQUATORIAL (R$)', type: 'num' },
    { label: 'COMISSÃO (R$)', type: 'num' },
    { label: 'Situação no mês', type: 'txt' },
    { label: 'Status do boleto', type: 'txt' },
    { label: 'Vencimento', type: 'date' },
    { label: 'Fatura nº', type: 'txt' },
    { label: 'Cliente no cadastro', type: 'txt' },
    { label: 'Origem', type: 'txt' },
    { label: 'Anotação da Gran', type: 'txt' },
    // Por último na tela: a URL é longa e empurrava situação e vencimento para fora.
    { label: 'LINK DO BOLETO', type: 'txt' },
  ];
  const rows = linhas.map(l => [
    l.motivos.join(' · '),
    l.grupo,
    l.numeroUc,
    l.boletoGran ?? l.semValorPorque,
    l.equatorial ?? '',
    l.comissao ?? '',
    l.boletoPrevisto ? `${rotuloEtapa(l.etapa)} (valor previsto)` : rotuloEtapa(l.etapa),
    l.statusBoleto,
    l.vencimento ? dateToSerial(l.vencimento) : '',
    l.faturaId !== null ? String(l.faturaId) : '',
    l.nomeCliente,
    l.origem === 'planilha' ? 'Modelo' : 'Extra',
    l.anotacao,
    l.link ?? '',
  ]);
  return { columns, rows, highlightCol: 0 };
}

export function gridCondominios(resumo: ResumoCondominio[]): GridModel {
  const columns: GridModel['columns'] = [
    { label: 'Condomínio', type: 'txt' },
    { label: 'UCs', type: 'txt' },
    { label: 'Prontas p/ emitir', type: 'txt' },
    { label: 'Emitidas', type: 'txt' },
    { label: 'Pagas', type: 'txt' },
    { label: 'Pendentes', type: 'txt' },
    { label: 'A conferir', type: 'txt' },
    { label: 'Boleto Gran (R$)', type: 'num' },
    { label: 'Equatorial (R$)', type: 'num' },
    { label: 'Comissão (R$)', type: 'num' },
  ];
  // Contagens vão como texto em coluna 'txt': a 'num' formata com 2 casas (e soma como R$).
  const rows = resumo.map(r => [
    r.grupo,
    String(r.ucs),
    String(r.etapas.pronta),
    String(r.etapas.emitida),
    String(r.etapas.paga),
    String(r.etapas.sem_fatura + r.etapas.em_andamento + r.etapas.sem_cobranca + r.etapas.cancelada),
    String(r.revisar),
    // Condomínio sem nenhum valor no mês fica em branco, não "R$ 0,00".
    r.semValor === r.ucs ? '' : r.boletoGran,
    r.semValor === r.ucs ? '' : r.equatorial,
    r.semValor === r.ucs ? '' : r.comissao,
  ]);
  return { columns, rows };
}

// ─────────────────────────── envio para a Gran ───────────────────────────

/** Linha no formato que vai para a Gran (e que fica guardado em `planilha_gran_envios`). */
export interface LinhaEnvioGran {
  nome: string;
  uc: string;
  boleto_gran: number | null;
  equatorial: number | null;
  comissao: number | null;
  link: string;
  situacao: string;
  vencimento: string;
  fatura_id: number | null;
}

export function linhasParaEnvio(linhas: LinhaPlanilhaGran[]): LinhaEnvioGran[] {
  return linhas.map(l => ({
    nome: l.grupo,
    uc: l.numeroUc,
    boleto_gran: l.boletoGran,
    equatorial: l.equatorial,
    comissao: l.comissao,
    link: l.link ?? '',
    // Boleto emitido e não pago: a Gran vê "A vencer" ou "Vencido", que é o que importa a ela.
    situacao: l.boletoGran === null ? l.semValorPorque
      : l.boletoPrevisto ? `${rotuloEtapa(l.etapa)} (valor previsto)`
        : l.etapa === 'emitida' && l.statusBoleto ? l.statusBoleto
          : rotuloEtapa(l.etapa),
    vencimento: l.vencimento,
    fatura_id: l.faturaId,
  }));
}

// ─────────────────────────── meses ───────────────────────────

/** Meses (canônicos, "SET/2026") em que alguma UC da lista tem fatura, do mais novo ao
 *  mais antigo. O mês corrente entra sempre, mesmo sem fatura ainda. */
export function mesesDisponiveis(itens: ItemPlanilhaGran[], ucs: UcPlanilhaGran[], faturas: FaturaPlanilhaGran[], hoje = new Date()): string[] {
  const ucPorId = new Map(ucs.map(u => [Number(u.id), u]));
  const chaves = new Set<string>();
  for (const item of itens) chavesDaUc(ucPorId.get(Number(item.unidade_consumidora_id)), item).forEach(k => chaves.add(k));
  const meses = new Set<string>([`${MESES_ABREV[hoje.getMonth()]}/${hoje.getFullYear()}`]);
  for (const f of faturas) {
    if (!chaves.has(normalizarUcFatura(f.uc_codigo))) continue;
    const m = normalizeMesReferencia(f.mes_referencia);
    if (/^[A-Z]{3}\/\d{4}$/.test(m)) meses.add(m);
  }
  const ordem = (m: string) => Number(m.slice(4)) * 12 + MESES_ABREV.indexOf(m.slice(0, 3));
  return Array.from(meses).sort((a, b) => ordem(b) - ordem(a));
}

/** Nome do arquivo que vai para a Gran, no padrão do modelo ("… Planilha 09 2026"). */
export function nomeArquivoGran(mes: string, interno = false): string {
  const m = normalizeMesReferencia(mes);
  const idx = MESES_ABREV.indexOf(m.slice(0, 3));
  const mm = idx >= 0 ? String(idx + 1).padStart(2, '0') : m.slice(0, 3);
  return `${interno ? 'Conferencia Vigor - ' : ''}Gran Energia - Planilha ${mm} ${m.slice(4)}.xlsx`;
}
